import * as THREE from 'three';
import type { TerrainProfile } from '../sim/terrain/profile';
import { instantiate, type ModelBatch } from './assets';

const POST_SPACING = 100;
const POST_SCALE = 2.3;
const VIEW_BEHIND = 30;
const VIEW_AHEAD = 70;

/** Numbered distance posts (Indonesian patok, Kiln GLB) every 100 m and the player's best-distance flag. */
export class Landmarks {
  readonly group = new THREE.Group();
  private readonly posts = new Map<number, THREE.Group>();
  private readonly flag: THREE.Group;
  private readonly flagLabel: THREE.Mesh;
  private flagDistance = -1;

  constructor(
    private readonly terrain: TerrainProfile,
    private readonly spawnX: number,
    private readonly postModel: ModelBatch[],
  ) {
    this.flag = new THREE.Group();
    const pole = new THREE.Mesh(new THREE.BoxGeometry(0.1, 3.6, 0.1), new THREE.MeshStandardMaterial({ color: '#2f2a26' }));
    pole.position.y = 1.8;
    this.flagLabel = new THREE.Mesh(new THREE.PlaneGeometry(2.2, 0.9), new THREE.MeshBasicMaterial({ transparent: true }));
    this.flagLabel.position.set(1.15, 3.1, 0);
    this.flag.add(pole, this.flagLabel);
    this.flag.visible = false;
    this.group.add(this.flag);
  }

  setBest(distance: number): void {
    if (distance <= 0) {
      this.flag.visible = false;
      return;
    }
    const rounded = Math.floor(distance);
    if (rounded !== this.flagDistance) {
      this.flagDistance = rounded;
      setLabel(this.flagLabel, `BEST ${rounded} m`, '#c2412b', '#fff4e0');
    }
    const x = this.spawnX + distance;
    this.flag.position.set(x, this.terrain.heightAt(x), 0.6);
    this.flag.visible = true;
  }

  update(cameraX: number): void {
    const first = Math.max(1, Math.ceil((cameraX - this.spawnX - VIEW_BEHIND) / POST_SPACING));
    const last = Math.floor((cameraX - this.spawnX + VIEW_AHEAD) / POST_SPACING);
    for (const [k, post] of this.posts) {
      if (k < first || k > last) {
        this.group.remove(post);
        disposeLabel(post.userData.label as THREE.Mesh);
        this.posts.delete(k);
      }
    }
    for (let k = first; k <= last; k++) if (!this.posts.has(k)) this.addPost(k);
  }

  private addPost(k: number): void {
    const x = this.spawnX + k * POST_SPACING;
    const post = new THREE.Group();
    // The patok is scaled up so it reads at the gameplay camera distance; the number sits on its face.
    const patok = instantiate(this.postModel, false);
    patok.scale.setScalar(POST_SCALE);
    // Same 256×104 aspect as the label canvas so the number isn't squashed.
    const label = new THREE.Mesh(new THREE.PlaneGeometry(0.3 * POST_SCALE, 0.122 * POST_SCALE), new THREE.MeshBasicMaterial({ transparent: true }));
    label.position.set(0, 0.62 * POST_SCALE, 0.085 * POST_SCALE + 0.01);
    setLabel(label, `${k * POST_SPACING}`, '#f1efe8', '#1d2a33', false, 86);
    post.add(patok, label);
    post.userData.label = label;
    post.position.set(x, this.terrain.heightAt(x), -1.2);
    this.posts.set(k, post);
    this.group.add(post);
  }
}

function setLabel(mesh: THREE.Mesh, text: string, bg: string, fg: string, border = true, fontPx = 52): void {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 104;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, 256, 104);
  if (border) {
    ctx.strokeStyle = fg;
    ctx.lineWidth = 6;
    ctx.strokeRect(6, 6, 244, 92);
  }
  ctx.fillStyle = fg;
  ctx.font = `800 ${fontPx}px "Arial Narrow", "Bahnschrift", system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, 128, 56);
  const material = mesh.material as THREE.MeshBasicMaterial;
  material.map?.dispose();
  material.map = new THREE.CanvasTexture(canvas);
  material.map.colorSpace = THREE.SRGBColorSpace;
  material.needsUpdate = true;
}

function disposeLabel(mesh: THREE.Mesh): void {
  const material = mesh.material as THREE.MeshBasicMaterial;
  material.map?.dispose();
  material.dispose();
  mesh.geometry.dispose();
}
